import { FlowNode } from '@holloway/dsl';

import { IconPalette, IconSettings, IconBolt, IconTrafficLights } from '@tabler/icons-react';

export interface NodeType {
  type: string;
  label: string;
  color: string;
  icon?: React.ReactNode;
  description?: string;
}

export interface NodeCategory {
  id: string;
  label: string;
  icon?: React.ReactNode;
  nodes: NodeType[];
}

// Modular node configuration - easy to extend
export const NODE_CATEGORIES: NodeCategory[] = [
  {
    id: 'flow',
    label: 'Flow Control',
    icon: <IconTrafficLights size={16} />,
    nodes: [
      { type: 'Start', label: 'Start', color: '#10b981', description: 'Entry point of the flow' },
      { type: 'End', label: 'End', color: '#ef4444', description: 'Exit point of the flow' },
    ],
  },
  {
    id: 'ui',
    label: 'UI Elements',
    icon: <IconPalette size={16} />,
    nodes: [
      { type: 'Page', label: 'Page', color: '#6366f1', description: 'Display a content page' },
    ],
  },
  {
    id: 'actions',
    label: 'Actions',
    icon: <IconBolt size={16} />,
    nodes: [
      { type: 'Email', label: 'Email', color: '#3b82f6', description: 'Send an email' },
      { type: 'Action', label: 'Action', color: '#2563eb', description: 'Generic action (Email/Webhook/SMS)' },
    ],
  },
];

// Flatten all nodes for easy lookup
export const ALL_NODE_TYPES: NodeType[] = NODE_CATEGORIES.flatMap((cat) => cat.nodes);

export function getNodeColor(type: string): string {
  return ALL_NODE_TYPES.find((n) => n.type === type)?.color || '#6b7280';
}

export function getNodeInfo(type: string): NodeType | undefined {
  return ALL_NODE_TYPES.find((n) => n.type === type);
}

export function createDefaultNode(type: string, id: string): Partial<FlowNode> {
  const base = { id, label: `${type} ${id}` };

  switch (type) {
    case 'Start':
      return { ...base, type: 'Start' as const };
    case 'End':
      return { ...base, type: 'End' as const };
    case 'Note':
      return { ...base, type: 'Note' as const, content: 'Enter your note here' };
    case 'Prompt':
      return { ...base, type: 'Prompt' as const, content: 'Enter your prompt', varId: '' };
    case 'Form':
      return {
        ...base,
        type: 'Form' as const,
        title: 'Form Title',
        fields: [{ name: 'field1', label: 'Field 1', type: 'text' as const, required: true }],
      };
    case 'If':
      return {
        ...base,
        type: 'If' as const,
        condition: { path: '', operator: '==' as const, value: 'yes' },
        then: '',
        else: '',
      };
    case 'Branch':
      return {
        ...base,
        type: 'Branch' as const,
        title: 'Choose an option',
        branches: [
          { label: 'Option 1', target: '' },
          { label: 'Option 2', target: '' },
        ],
      };
    case 'Switch':
      return {
        ...base,
        type: 'Switch' as const,
        cases: [{ condition: { path: '', operator: '==' as const, value: 'a' }, next: '' }],
        defaultNext: '',
      };
    case 'SetVar':
      return { ...base, type: 'SetVar' as const, varId: '', valueTemplate: 'value' };
    case 'Action':
      return {
        ...base,
        type: 'Action' as const,
        action: {
          type: 'Email' as const,
          to: '',
          subject: 'Subject',
          body: 'Body',
          dedupeKeyTemplate: 'email-action',
        },
      };
    // UI Elements
    case 'Page':
      return {
        ...base,
        type: 'Page' as const,
        title: 'Page Title',
        content: 'Page content goes here',
        layoutType: 'single-column' as const,
        contactMenu: {
          enableCallNow: false,
          enableCallReturn: false,
          enableEmail: false,
        },
        elements: [],
      };
    case 'ContactUs':
      return {
        ...base,
        type: 'ContactUs' as const,
        title: 'Contact Us',
        description: 'Get in touch with us',
        fields: [
          { name: 'name', label: 'Name', type: 'text' as const, required: true },
          { name: 'email', label: 'Email', type: 'email' as const, required: true },
          { name: 'message', label: 'Message', type: 'textarea' as const, required: true },
        ],
        submitButtonText: 'Submit',
      };
    case 'TwoLevelMenu':
      return {
        ...base,
        type: 'TwoLevelMenu' as const,
        title: 'Main Menu',
        items: [
          {
            label: 'Category 1',
            target: '',
            items: [
              { label: 'Item 1.1', target: '' },
              { label: 'Item 1.2', target: '' },
            ],
          },
          {
            label: 'Category 2',
            target: '',
            items: [
              { label: 'Item 2.1', target: '' },
            ],
          },
        ],
      };
    // Logic Elements
    case 'RpaRecorder':
      return {
        ...base,
        type: 'RpaRecorder' as const,
        description: 'RPA automation task',
        outputVarId: '',
      };
    case 'Allocator':
      return {
        ...base,
        type: 'Allocator' as const,
        resourceType: 'agent',
        allocationStrategy: 'round-robin' as const,
        targetVarId: '',
      };
    case 'DecisionPoint':
      return {
        ...base,
        type: 'DecisionPoint' as const,
        title: 'Decision Point',
        description: 'Route based on criteria',
        criteria: [
          {
            condition: { path: 'session.score', operator: '==' as const, value: 'high' },
            label: 'High Priority',
            next: '',
          },
          {
            condition: { path: 'session.score', operator: '==' as const, value: 'low' },
            label: 'Low Priority',
            next: '',
          },
        ],
      };
    case 'Email':
      return {
        ...base,
        type: 'Action' as const,
        action: {
          type: 'Email' as const,
          to: '{{session.email}}',
          subject: 'Email Subject',
          body: 'Email body content',
          dedupeKeyTemplate: 'email-{{session.email}}',
        },
      };
    default:
      // For unknown types, return a Page node as fallback
      return {
        ...base,
        type: 'Page' as const,
        title: 'Page Title',
        content: 'Page content goes here',
        layoutType: 'single-column' as const,
        contactMenu: {
          enableCallNow: false,
          enableCallReturn: false,
          enableEmail: false,
        },
        elements: [],
      };
  }
}
